import { Column, Group } from '@studio/design-system/form';
import { Input } from '@studio/design-system/input';
import * as Switch from '@studio/design-system/switch';
import { Credentials as CredentialsComponent } from '#~/common/editor/credentials.js';
import { Sel, STagNew } from '#~/stag/tag.js';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import { toAccessor } from '@studio/react-utils';

interface DestinationFormProps {
  nickname: string;
  setNickname: (v: string) => void;
  server: string;
  setServer: (v: string) => void;
  streamKey: string;
  setStreamKey: (v: string) => void;
  requireAuth: boolean;
  setRequireAuth: (v: boolean) => void;
  credentials: M_Credentials | undefined;
  setCredentials: (v: M_Credentials | undefined) => void;
  errors: Record<string, string>;
}

export const DestinationForm = ({
  nickname,
  setNickname,
  server,
  setServer,
  streamKey,
  setStreamKey,
  requireAuth,
  setRequireAuth,
  credentials,
  setCredentials,
  errors
}: DestinationFormProps) => {
  return (
    <>
      <div className='max-w-[480px] flex-1 overflow-auto p-40'>
        <p className='mb-24 text-md font-medium leading-[14px] text-light'>Video Settings</p>
        <Column className='gap-24'>
          {/* Group for entering a nickname for the RTMP destination */}
          <Group
            className='rounded-md text-sm font-medium leading-[12px] text-light'
            label='Destination Nickname'
            annotation={errors.nickname}
          >
            <Input
              placeholder='Youtube'
              value={nickname}
              onChange={setNickname}
              variant={errors.nickname ? 'error' : undefined}
              className=''
              {...STagNew(Sel.ProductionOutput, 'rtmp-destination-nickname')}
            />
          </Group>

          {/* Group for entering the RTMP server URL */}
          <Group
            className='rounded-md text-sm font-medium leading-[12px] text-light'
            label='Server'
            annotation={errors.server}
          >
            <Input
              placeholder='rtmp://a.rtmp.youtube.com/live2'
              value={server}
              onChange={setServer}
              variant={errors.server ? 'error' : undefined}
              className=''
              {...STagNew(Sel.ProductionOutput, 'rtmp-destination-server')}
            />
          </Group>

          {/* Group for entering the stream key for authentication */}
          <Group
            className='rounded-md text-sm font-medium leading-[12px] text-light'
            label='Stream Key'
            annotation={errors.streamKey}
          >
            <Input
              placeholder='<stream-key>'
              value={streamKey}
              onChange={setStreamKey}
              variant={errors.streamKey ? 'error' : undefined}
              className=''
              {...STagNew(Sel.ProductionOutput, 'rtmp-destination-stream-key')}
            />
          </Group>

          {/* Add this back once we support this */}
          {false && (
            <Group className='rounded-8 bg-base-100 p-8'>
              <Switch.Container
                className='bg-base-100 text-sm font-medium'
                label='Require Authentication'
              >
                <Switch.Archetype checked={requireAuth} onCheckedChange={setRequireAuth} />
              </Switch.Container>
              {/* On the backend, we don't currently support credentials, so it doesn't matter what value we have here */}
              {requireAuth && (
                <>
                  <div className='mb-12 w-full border-t border-base-50' />
                  <CredentialsComponent
                    credentials={toAccessor(credentials, setCredentials)}
                    className='mb-4'
                    labelClassName='text-light'
                    inputClassName='bg-base-50 text-light rounded-8'
                    inputSize='md'
                  />
                </>
              )}
            </Group>
          )}

          {errors.general && <div className='text-sm text-error'>{errors.general}</div>}
        </Column>
      </div>
    </>
  );
};
